import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State = "loading" | "valid" | "already" | "invalid" | "submitting" | "done" | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState<State>("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } }
        );
        const data = await res.json();
        if (data.valid) setState("valid");
        else if (data.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const confirm = async () => {
    setState("submitting");
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", { body: { token } });
    if (error || !data?.success) {
      setErrorMsg(error?.message || "Could not process unsubscribe.");
      setState("error");
    } else {
      setState("done");
    }
  };

  return (
    <main className="min-h-screen bg-jungle text-background flex items-center justify-center px-6">
      <div className="max-w-lg w-full border border-background/15 bg-background/5 p-10 backdrop-blur">
        <p className="text-xs uppercase tracking-[0.4em] text-gold mb-6">— Email Preferences</p>
        <h1 className="font-display text-4xl mb-6">Unsubscribe</h1>

        {state === "loading" && <p className="text-background/70">Validating your link…</p>}

        {state === "invalid" && (
          <p className="text-background/70">This unsubscribe link is invalid or has expired.</p>
        )}

        {state === "already" && (
          <p className="text-background/70">You've already been unsubscribed. No further emails will be sent.</p>
        )}

        {state === "valid" && (
          <>
            <p className="text-background/75 mb-8 leading-relaxed">
              Click below to confirm you'd like to stop receiving emails from Guide Francophone Siem Reap.
            </p>
            <button
              onClick={confirm}
              className="w-full bg-gold text-ink py-4 text-xs uppercase tracking-[0.3em] hover:bg-background transition-colors"
            >
              Confirm Unsubscribe
            </button>
          </>
        )}

        {state === "submitting" && <p className="text-background/70">Processing…</p>}

        {state === "done" && (
          <p className="text-background/75">You've been unsubscribed. We're sorry to see you go.</p>
        )}

        {state === "error" && <p className="text-red-300">{errorMsg}</p>}
      </div>
    </main>
  );
};

export default Unsubscribe;
